import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontFamily: "Helvetica",
    fontSize: 11,
    color: "#1f2530",
    gap: 14,
  },
  header: {
    borderBottomWidth: 2,
    borderBottomColor: "#25374f",
    borderStyle: "solid",
    paddingBottom: 10,
    marginBottom: 10,
  },
  title: {
    fontSize: 18,
    color: "#25374f",
    fontWeight: "bold",
  },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 6,
  },
  metaItem: {
    fontSize: 10,
    color: "#4b5563",
  },
  section: {
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#25374f",
    marginBottom: 8,
  },
  summaryGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  summaryBlock: {
    flexBasis: "23%",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderStyle: "solid",
    borderRadius: 6,
    padding: 10,
  },
  summaryLabel: {
    color: "#4b5563",
    marginBottom: 4,
    fontSize: 10,
  },
  summaryValue: {
    fontWeight: "bold",
    fontSize: 14,
    marginBottom: 6,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#25374f",
    color: "white",
    padding: 8,
    fontWeight: "bold",
    fontSize: 10,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#e5e7eb",
    borderStyle: "solid",
  },
  colLabel: { width: "30%" },
  colValue: { width: "20%" },
  colBasis: { width: "50%" },
  colMedium: { width: "33%" },
  colSmall: { width: "17%" },
  muted: {
    color: "#6b7280",
  },
  note: {
    color: "#4b5563",
    fontSize: 10,
    marginTop: 4,
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 28,
    right: 28,
    fontSize: 9,
    color: "#6b7280",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  placeholderBox: {
    borderWidth: 1,
    borderColor: "#9ca3af",
    borderStyle: "dashed",
    borderRadius: 6,
    height: 340,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 12,
  },
  placeholderLabel: {
    color: "#6b7280",
    fontSize: 12,
  },
});

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const formatCount = (value) => {
  const num = toNumber(value);
  return Number.isFinite(num) ? num.toLocaleString() : "—";
};

const formatArea = (value) => {
  const num = toNumber(value);
  if (num === null) return "—";
  return `${num.toLocaleString()} m²`;
};

const formatStandard = (basis, perM2, personsPerSpace, perPerson) => {
  if (!basis) return "—";
  if (basis === "per_m2") {
    const val = toNumber(perM2);
    return val ? `1 per ${val.toLocaleString()} m²` : "—";
  }
  if (basis === "persons_per_space") {
    const val = toNumber(personsPerSpace);
    return val ? `1 per ${val.toLocaleString()} people` : "—";
  }
  if (basis === "per_person") {
    const val = toNumber(perPerson);
    if (!val) return "—";
    const digits = val >= 1 ? 1 : 2;
    return `${val.toLocaleString(undefined, { maximumFractionDigits: digits })} spaces per person`;
  }
  return "—";
};

const MetadataHeader = ({ project }) => {
  const projectName = project?.name || "Pebbledash project";
  const buildingUse = project?.building_use || "Office";
  const location = project?.local_authority || project?.postcode || "Location unknown";
  const dateLabel = new Date().toLocaleDateString();

  return (
    <View style={styles.header}>
      <Text style={styles.title}>Pebbledash Cycle Spaces Report</Text>
      <View style={styles.metaRow}>
        <Text style={styles.metaItem}>Project: {projectName}</Text>
        <Text style={styles.metaItem}>Location: {location}</Text>
        <Text style={styles.metaItem}>Building use: {buildingUse}</Text>
        <Text style={styles.metaItem}>Date: {dateLabel}</Text>
      </View>
    </View>
  );
};

const SummaryBlock = ({ label, value, note }) => (
  <View style={styles.summaryBlock}>
    <Text style={styles.summaryLabel}>{label}</Text>
    <Text style={styles.summaryValue}>{value}</Text>
    {note ? <Text style={styles.note}>{note}</Text> : null}
  </View>
);

const RequirementsTable = ({ result, accessibleSpaces, cargoSpaces }) => {
  const longStay = result?.requirements?.long_stay_spaces;
  const shortStay = result?.requirements?.short_stay_spaces;

  const rows = [
    {
      label: "Long-stay",
      spaces: formatCount(longStay),
      basis: formatStandard(
        result?.standard_applied?.long_stay_basis,
        result?.standard_applied?.long_stay_per_m2,
        result?.standard_applied?.long_stay_persons_per_space,
        result?.standard_applied?.long_stay_per_person,
      ),
    },
  ];

  if (shortStay != null) {
    rows.push({
      label: "Short-stay",
      spaces: formatCount(shortStay),
      basis: formatStandard(
        result?.standard_applied?.short_stay_basis,
        result?.standard_applied?.short_stay_per_m2,
        result?.standard_applied?.short_stay_persons_per_space,
        result?.standard_applied?.short_stay_per_person,
      ),
    });
  }

  if (accessibleSpaces != null) {
    rows.push({
      label: "Accessible provision",
      spaces: formatCount(accessibleSpaces),
      basis: `${formatCount(accessibleSpaces)} spaces (min ${result?.design_checks?.accessible_percent_min}% of total)`,
    });
  }

  if (cargoSpaces != null) {
    rows.push({
      label: "Cargo / adapted",
      spaces: formatCount(cargoSpaces),
      basis: `${result?.design_checks?.cargo_or_adapted_percent}% of total provision`,
    });
  }

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Cycle parking summary</Text>
      <View style={styles.tableHeader}>
        <Text style={styles.colLabel}>Category</Text>
        <Text style={styles.colValue}>Spaces</Text>
        <Text style={styles.colBasis}>Standard applied</Text>
      </View>
      {rows.map((row) => (
        <View key={row.label} style={styles.tableRow}>
          <Text style={styles.colLabel}>{row.label}</Text>
          <Text style={styles.colValue}>{row.spaces}</Text>
          <Text style={styles.colBasis}>{row.basis}</Text>
        </View>
      ))}
    </View>
  );
};

const InputSummary = ({ result }) => {
  const areaMetric = result?.standard_applied?.area_metric?.toUpperCase?.() || result?.inputs?.area_metric?.toUpperCase?.();
  const areaBasis = areaMetric === "GEA" ? "Gross External Area" : "Gross Internal Area";

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Inputs & assumptions</Text>
      <View style={styles.tableHeader}>
        <Text style={styles.colMedium}>Metric</Text>
        <Text style={styles.colMedium}>Value</Text>
        <Text style={styles.colMedium}>Notes</Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.colMedium}>Assessed area ({areaMetric || "GIA"})</Text>
        <Text style={styles.colMedium}>{formatArea(result?.inputs?.area_used_m2)}</Text>
        <Text style={styles.colMedium}>{areaBasis}</Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.colMedium}>Provided GEA</Text>
        <Text style={styles.colMedium}>{formatArea(result?.inputs?.gea_m2)}</Text>
        <Text style={styles.colMedium}>Used where required by local guidance</Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.colMedium}>Provided NIA</Text>
        <Text style={styles.colMedium}>{formatArea(result?.inputs?.nia_m2)}</Text>
        <Text style={styles.colMedium}>BCO fallback for staff estimation</Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.colMedium}>Occupancy ratio</Text>
        <Text style={styles.colMedium}>
          {result?.inputs?.occupancy_ratio_sqm_per_person
            ? `${result.inputs.occupancy_ratio_sqm_per_person.toLocaleString()} m²/person`
            : "—"}
        </Text>
        <Text style={styles.colMedium}>Inferred where provided</Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.colMedium}>Estimated staff</Text>
        <Text style={styles.colMedium}>{formatCount(result?.inputs?.estimated_persons)}</Text>
        <Text style={styles.colMedium}>Derived from occupancy or NIA</Text>
      </View>
    </View>
  );
};

const AuthorityInfo = ({ result }) => {
  const authority = result?.authority;
  const sources = Array.isArray(authority?.sources) ? authority.sources : [];
  const ptalLabel = authority?.ptal?.label || authority?.ptal?.ptal || result?.standard_applied?.ptal?.label;

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Standard & authority</Text>
      <View style={styles.tableHeader}>
        <Text style={styles.colMedium}>Item</Text>
        <Text style={styles.colMedium}>Detail</Text>
        <Text style={styles.colMedium}>Notes</Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.colMedium}>Authority</Text>
        <Text style={styles.colMedium}>{authority?.name || authority?.code || "—"}</Text>
        <Text style={styles.colMedium}>{ptalLabel ? `PTAL: ${ptalLabel}` : ""}</Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.colMedium}>Area basis</Text>
        <Text style={styles.colMedium}>{result?.standard_applied?.area_metric?.toUpperCase?.() || "GIA"}</Text>
        <Text style={styles.colMedium}>
          {result?.standard_applied?.area_metric === "gea"
            ? "Gross External Area requirement"
            : "Gross Internal Area requirement"}
        </Text>
      </View>
      <View style={styles.tableRow}>
        <Text style={styles.colMedium}>Sources</Text>
        <Text style={styles.colMedium}>
          {sources.length > 0 ? sources.map((s) => s.title).join(" · ") : "—"}
        </Text>
        <Text style={styles.colMedium}>References from the selected authority</Text>
      </View>
    </View>
  );
};

const CyclePDFDocument = ({ project, result }) => {
  const longStay = toNumber(result?.requirements?.long_stay_spaces);
  const shortStay = toNumber(result?.requirements?.short_stay_spaces);
  const totalSpaces = (longStay || 0) + (shortStay || 0);

  const accessiblePercent = toNumber(result?.design_checks?.accessible_percent_min);
  const cargoPercent = toNumber(result?.design_checks?.cargo_or_adapted_percent);

  const accessibleSpaces =
    accessiblePercent !== null && totalSpaces
      ? Math.max(1, Math.ceil((accessiblePercent / 100) * totalSpaces))
      : null;
  const cargoSpaces =
    cargoPercent !== null && totalSpaces ? Math.max(1, Math.ceil((cargoPercent / 100) * totalSpaces)) : null;

  return (
    <Document>
      <Page size="A3" orientation="landscape" style={styles.page}>
        <MetadataHeader project={project} />

        <View style={styles.summaryGrid}>
          <SummaryBlock label="Long-stay spaces" value={formatCount(longStay)} />
          {shortStay !== null ? <SummaryBlock label="Short-stay spaces" value={formatCount(shortStay)} /> : null}
          <SummaryBlock label="Total provision" value={formatCount(totalSpaces)} />
          {accessibleSpaces !== null ? (
            <SummaryBlock
              label="Accessible minimum"
              value={formatCount(accessibleSpaces)}
              note={`At least ${accessiblePercent}% of total`}
            />
          ) : null}
          {cargoSpaces !== null ? (
            <SummaryBlock
              label="Cargo / adapted"
              value={formatCount(cargoSpaces)}
              note={`${cargoPercent}% allowance for cargo/adapted cycles`}
            />
          ) : null}
        </View>

        <RequirementsTable
          result={result}
          accessibleSpaces={accessibleSpaces}
          cargoSpaces={cargoSpaces}
        />

        <InputSummary result={result} />

        <AuthorityInfo result={result} />

        <View style={styles.footer} fixed>
          <Text>Generated by Pebbledash</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} />
        </View>
      </Page>

      <Page size="A3" orientation="landscape" style={styles.page}>
        <MetadataHeader project={project} />

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Key cycle summary</Text>
          <View style={styles.summaryGrid}>
            <SummaryBlock label="Long-stay provision" value={formatCount(longStay)} />
            {shortStay !== null ? <SummaryBlock label="Short-stay provision" value={formatCount(shortStay)} /> : null}
            <SummaryBlock label="Total provision" value={formatCount(totalSpaces)} />
            {accessibleSpaces !== null ? (
              <SummaryBlock label="Accessible minimum" value={formatCount(accessibleSpaces)} />
            ) : null}
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Layout placeholder</Text>
          <Text style={styles.note}>
            Reserve this space for a future plan or diagram of the proposed cycle parking arrangement.
          </Text>
          <View style={styles.placeholderBox}>
            <Text style={styles.placeholderLabel}>Cycle parking layout placeholder</Text>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text>Generated by Pebbledash</Text>
          <Text render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default CyclePDFDocument;
